import { useRemindersAPI } from "@/app/hooks/api_hooks/Vaccination/useRemindersAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useUtils } from "@/app/hooks/useUtils";
import { useRouter } from "@/app/hooks/useRouter";
import { ROUTES } from "@/app/routes";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useState } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { IoMdAddCircleOutline } from "react-icons/io";
import { useNavigate } from "react-router";
import RBACFlag from "@/app/components/core/RBACFlag";

export interface InvoiceFilter {
  status: Array<string>;
}

// Single hardcoded fallback used only when the API sends no icon for a section.
const DEFAULT_ICON =
  "https://vetic-img.s3.ap-south-1.amazonaws.com/quick_commerce//misc/doctorReminder.png";

const getConfig = (key: string, section?: { title?: string; icon?: string | null }) => ({
  title: section?.title,
  icon: section?.icon ?? DEFAULT_ICON,
  showDoctorColumn: key === "DOCTOR",
});

const RemindersTab = () => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ DOCTOR: true });
  const { routeState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { customerId, petId, reminders, petDetails } = routeState.state as ICustomerProfileState;
  const { execute: fetchReminders } = useRemindersAPI();
  const { veticDateFormat } = useUtils();
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();

  const handelAddReminder = () => {
    navigate(`${getRouteKey("REMINDERS_CREATION", "url")}/${customerId}/${petId}`);
  };

  useEffect(() => {
    if (petId && customerId) fetchReminders(customerId, petId);
  }, [petId, customerId]);

  const handleToggle = (key: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set("document_source", `${key.toLowerCase()}_reminder`);
    window.history.replaceState({}, "", url.toString());
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const source = new URL(window.location.href).searchParams.get("document_source");
    const rawKey = source ? source.replace(/_reminder$/, "").toUpperCase() : "DOCTOR";
    const keyAliases: Record<string, string> = { VACCINATION: "VACCINE" };
    const key = keyAliases[rawKey] ?? rawKey;
    setExpanded({ [key]: true });
  }, []);

  const allKeys = Object.keys(reminders ?? {});

  return (
    <div className="pl-2">
      <div className="flex items-center justify-between gap-3 flex-wrap py-1">
        <h2 className="flex-1 min-w-0 text-sm text-black font-semibold pt-2 break-words">
          Reminders For "{petDetails?.name}"
        </h2>
        <RBACFlag actionId="ADD_REMINDER_ACTION_BUTTON">
          <button
            className="bg-[#000000] text-white px-6 py-2 rounded-lg text-xs font-medium cursor-pointer items-center justify-center flex gap-2 shrink-0"
            onClick={() => handelAddReminder()}
          >
            <IoMdAddCircleOutline className="w-4 h-4" />
            Add Reminder
          </button>
        </RBACFlag>
      </div>
      {allKeys.map((key, index) => {
        const section = reminders?.[key];
        const config = getConfig(key, section);
        const isExpanded = !!expanded[key];
        const list = section?.reminders;
        const showMedicineColumn = !!list?.some((item) => item?.medicine_type);
        const gridStyle = {
          gridTemplateColumns: [
            "1fr",
            "2fr",
            ...(config.showDoctorColumn ? ["2fr"] : []),
            "2fr",
            ...(showMedicineColumn ? ["2fr"] : []),
            "1fr",
          ].join(" "),
        };

        return (
          <div
            key={key}
            className={`my-2 ${index === 0 ? "" : "mt-5"} ${index === allKeys.length - 1 ? "mb-10" : ""}`}
          >
            <div className="border border-[#e6e8ec] rounded-xl overflow-hidden">
              <div
                className={`flex items-center justify-between gap-2 border-[#e6e8ec] p-3 cursor-pointer ${
                  isExpanded ? "bg-white" : "bg-[#F5F5F5]"
                }`}
                onClick={() => handleToggle(key)}
              >
                <div className="flex gap-3 items-center">
                  <img src={config.icon} alt={config.title} width={30} height={30} />
                  <div className="text-black text-sm font-semibold">{config.title}</div>
                </div>
                <div className="flex gap-1 items-center cursor-pointer underline underline-offset-2">
                  <div className="text-black text-xs">{isExpanded ? "Hide all" : "Show all"}</div>
                  <div className="font-semibold">
                    {isExpanded ? <IoIosArrowUp color="black" /> : <IoIosArrowDown color="black" />}
                  </div>
                </div>
              </div>
              {isExpanded &&
                (list && list.length > 0 ? (
                  <div className="bg-[#F5F5F5] pt-4 sm:mt-0">
                    <div
                      className="hidden sm:grid items-center gap-0 mx-3 px-2 py-4 text-sm font-medium text-gray-500"
                      style={gridStyle}
                    >
                      <p>S.NO</p>
                      <p>Reminder Name</p>
                      {config.showDoctorColumn && <p>Doctor Name</p>}
                      <p>Due On</p>
                      {showMedicineColumn && <p>Medicine Type</p>}
                      <p></p>
                    </div>
                    <div className="space-y-1 px-3 pb-4">
                      {list.map((item, itemIndex) => (
                        <div
                          key={itemIndex}
                          className="flex flex-col gap-2 p-4 bg-white border border-[#e6e8ec] rounded-xl text-xs text-black sm:grid sm:items-center sm:gap-0"
                          style={gridStyle}
                        >
                          <div className="grid grid-cols-[110px_1fr] items-start gap-x-4 sm:block">
                            <span className="text-gray-500 font-medium sm:hidden">S.NO</span>
                            <p>{itemIndex + 1}</p>
                          </div>
                          <div className="grid grid-cols-[110px_1fr] items-start gap-x-4 sm:block">
                            <span className="text-gray-500 font-medium sm:hidden">Reminder Name</span>
                            <p>{item?.reminder_name}</p>
                          </div>
                          {config.showDoctorColumn && (
                            <div className="grid grid-cols-[110px_1fr] items-start gap-x-4 sm:block">
                              <span className="text-gray-500 font-medium sm:hidden">Doctor Name</span>
                              <p>{item?.doctor_name || "-"}</p>
                            </div>
                          )}
                          <div className="grid grid-cols-[110px_1fr] items-start gap-x-4 sm:block">
                            <span className="text-gray-500 font-medium sm:hidden">Due On</span>
                            <p>{veticDateFormat(item?.due_date)}</p>
                          </div>
                          {showMedicineColumn && (
                            <div className="grid grid-cols-[110px_1fr] items-start gap-x-4 sm:block">
                              <span className="text-gray-500 font-medium sm:hidden">Medicine Type</span>
                              <p>{item?.medicine_type || "-"}</p>
                            </div>
                          )}
                          <div className="w-full sm:w-auto grid grid-cols-[110px_1fr] items-center gap-x-4 sm:flex sm:justify-start">
                            <span className="text-gray-500 font-medium sm:hidden">Status</span>
                            <div
                              className={`w-fit text-xs font-semibold px-3 py-1 rounded-2xl ${
                                item?.status === "OVERDUE"
                                  ? "bg-rose-50 text-red-600"
                                  : item?.status === "OPEN"
                                    ? "bg-[#edffe8] text-[#38890d]"
                                    : item?.status === "EXPIRED"
                                      ? "bg-gray-200 text-neutral-400"
                                      : "bg-white text-black"
                              }`}
                            >
                              {item?.status}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-b-xl px-3 py-6">
                    <p className="text-xs text-center text-gray-400">No Reminders</p>
                  </div>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RemindersTab;